import type {
  FinancialAccount,
  FinancialConnection,
} from "@/src/features/api/memberApi";

export const formatAccountMask = (mask?: string): string => {
  if (!mask?.trim()) {
    return "····";
  }
  return `····${mask.trim()}`;
};

/** FinancialAccount.status: active when missing or "0". */
export const isActiveFinancialAccount = (account: FinancialAccount): boolean =>
  (account.status ?? "0") === "0";

/** Invest Portfolio sub-account: INVESTMENT type and active status. */
export const isInvestPortfolioFinancialAccount = (
  account: FinancialAccount,
): boolean =>
  account.type === "INVESTMENT" && isActiveFinancialAccount(account);

export interface GetConnectionAccountMasksOptions {
  /**
   * Invest Portfolio Manage card: only active INVESTMENT sub-accounts.
   * Aligns with connection-level `filterConnectionsForInvestPortfolio`.
   */
  investPortfolioOnly?: boolean;
  /** When true, only active (`status === "0"`) sub-accounts. */
  activeOnly?: boolean;
}

/** Account masks for display under a financial connection. */
export const getConnectionAccountMasks = (
  connection: FinancialConnection,
  options: GetConnectionAccountMasksOptions = {},
): string[] => {
  const { investPortfolioOnly = false, activeOnly = false } = options;

  return (
    connection.accounts
      ?.filter((account) => {
        if (investPortfolioOnly) {
          return isInvestPortfolioFinancialAccount(account);
        }
        if (activeOnly) {
          return isActiveFinancialAccount(account);
        }
        return true;
      })
      .map((account) => account.account_mask)
      .filter((mask): mask is string => Boolean(mask?.trim())) ?? []
  );
};

export const chunkAccountMaskRows = (
  masks: string[],
  rowSize = 3,
): string[][] => {
  if (masks.length === 0) {
    return [];
  }

  const rows: string[][] = [];
  for (let index = 0; index < masks.length; index += rowSize) {
    rows.push(masks.slice(index, index + rowSize));
  }
  return rows;
};

export interface AccountDisplayInfo {
  name: string;
  mask: string;
  type: string;
}

export interface GetConnectionDisplayInfoOptions {
  /** "invest" → INVESTMENT type accounts only; "non_invest" → non-INVESTMENT accounts only. */
  section: "invest" | "non_invest";
}

/**
 * Filter a connection's sub-accounts by section and return display info (name + mask + type).
 * - section="invest": active INVESTMENT accounts only
 * - section="non_invest": active non-INVESTMENT accounts only
 */
export const getConnectionDisplayInfo = (
  connection: FinancialConnection,
  options: GetConnectionDisplayInfoOptions,
): AccountDisplayInfo[] => {
  const { section } = options;

  return (
    connection.accounts
      ?.filter(isActiveFinancialAccount)
      .filter((account) => {
        if (section === "invest") {
          return account.type === "INVESTMENT";
        }
        return account.type != null && account.type !== "INVESTMENT";
      })
      .map((account) => ({
        name: account.account_name?.trim() || "Account",
        mask: formatAccountMask(account.account_mask),
        type: account.type ?? "OTHER",
      })) ?? []
  );
};
